import {
  Avatar,
  AvatarFallback,
  Button,
  DestructiveConfirm,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FilterBar,
  type FilterBarDef,
  type FilterBarSortField,
  type FilterBarValue,
  MobileListCard,
  Skeleton,
  type SortFieldDef,
  Spinner,
  type SwipeAction,
  useClientSort,
} from '@avila-roffe/components-web';
import { Activity, Archive, Pencil, Plus, Search, Tag, Trash2 } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { useChatUser } from './ChatUserContext';
import type { ChatSession, SessionStatus } from './chatApi';
import { useChatSessionStore } from './chatStore';
import { useCloseSession, useDeleteSession, useUpdateSession } from './hooks/useChatSessions';

// ─── Sort + Filter ────────────────────────────────────────────────────────────

const SORT_FIELD_DEFS: SortFieldDef<ChatSession>[] = [
  { field: 'title', label: 'Name', getValue: (s) => s.title || s.agentName },
  {
    field: 'lastActivityAt',
    label: 'Updated',
    getValue: (s) => s.lastActivityAt,
    defaultDir: 'desc',
  },
];

const FILTER_BAR_SORT_FIELDS: FilterBarSortField[] = SORT_FIELD_DEFS.map((f) => ({
  field: f.field,
  label: f.label,
}));

function filterSession(session: ChatSession, filters: FilterBarValue[]): boolean {
  for (const f of filters) {
    if (f.key === 'name') {
      const q = f.value.toLowerCase();
      if (!session.title.toLowerCase().includes(q) && !session.agentName.toLowerCase().includes(q))
        return false;
    }
    if (f.key === 'agent' && session.agentName !== f.value) return false;
    if (f.key === 'status' && session.status !== f.value) return false;
    if (f.key === 'tenant' && session.tenantSlug !== f.value) return false;
  }
  return true;
}

function relativeTime(isoString: string): string {
  try {
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return '';
  }
}

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ConversationSidebarProps {
  sessions: ChatSession[];
  isLoading: boolean;
  isError: boolean;
  activeSessionId?: string | null;
  onSelect: (id: string) => void;
  onNewSession?: () => void;
}

// ─── SessionItem ──────────────────────────────────────────────────────────────

interface SessionItemProps {
  session: ChatSession;
  isActive: boolean;
  isSuperAdmin: boolean;
  onSelect: (id: string) => void;
}

function SessionItem({ session, isActive, isSuperAdmin, onSelect }: SessionItemProps) {
  const deleteSession = useDeleteSession();
  const archiveSession = useCloseSession();
  const updateSession = useUpdateSession();

  const [renameOpen, setRenameOpen] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function openRename() {
    setRenameValue(session.title || '');
    setRenameOpen(true);
    setTimeout(() => inputRef.current?.select(), 50);
  }

  function commitRename() {
    const trimmed = renameValue.trim();
    if (trimmed && trimmed !== session.title) {
      updateSession.mutate({ id: session.id, patch: { title: trimmed } });
    }
    setRenameOpen(false);
  }

  const swipeActions: SwipeAction[] = [
    {
      label: 'Rename',
      icon: <Pencil className="size-4" />,
      onClick: openRename,
      className: 'bg-muted-foreground',
    },
    ...(session.status === 'active'
      ? [
          {
            label: 'Archive',
            icon: <Archive className="size-4" />,
            onClick: () => archiveSession.mutate(session.id),
            className: 'bg-amber-500',
          },
        ]
      : []),
    {
      label: 'Delete',
      icon: <Trash2 className="size-4" />,
      onClick: () => setDeleteOpen(true),
      className: 'bg-destructive',
      destructive: true,
    },
  ];

  const agentInitials = session.agentName ? session.agentName.slice(0, 2).toUpperCase() : '?';

  return (
    <>
      <div data-testid={`conversation-item-${session.id}`}>
        <MobileListCard
          onClick={() => onSelect(session.id)}
          swipeActions={swipeActions}
          desktopActionsMode="menu"
          aria-label={session.title || 'Untitled'}
          className={[
            '!rounded-lg !border !p-0 !gap-0 !flex-col !items-stretch text-sm transition-colors',
            isActive
              ? '!border-primary !bg-primary/10'
              : '!border-transparent hover:!bg-muted hover:!border-border',
          ].join(' ')}
        >
          <div className="w-full px-3 py-2 text-left flex items-center gap-2">
            <Avatar size="xs" className="shrink-0 opacity-60">
              <AvatarFallback className="text-[9px]">{agentInitials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0 pr-6">
              <span className="block font-medium truncate">{session.title || 'Untitled'}</span>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-[10px] text-muted-foreground capitalize">
                  {isSuperAdmin && session.tenantSlug ? session.tenantSlug : session.status}
                </span>
                <span className="text-[10px] text-muted-foreground/50">·</span>
                <span className="text-[10px] text-muted-foreground">
                  {relativeTime(session.lastActivityAt)}
                </span>
              </div>
            </div>
          </div>
        </MobileListCard>
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Rename conversation</DialogTitle>
          </DialogHeader>
          <input
            ref={inputRef}
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') setRenameOpen(false);
            }}
            placeholder="Conversation name"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <DialogFooter>
            <Button appearance="outline" size="sm" onClick={() => setRenameOpen(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={commitRename}
              disabled={!renameValue.trim() || updateSession.isPending}
            >
              {updateSession.isPending ? <Spinner className="size-4" /> : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DestructiveConfirm
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete conversation?"
        description="This permanently deletes all messages. This cannot be undone."
        onConfirm={() =>
          deleteSession.mutate(session.id, { onSuccess: () => setDeleteOpen(false) })
        }
        isPending={deleteSession.isPending}
      />
    </>
  );
}

// ─── ConversationSidebar ──────────────────────────────────────────────────────

export function ConversationSidebar({
  sessions,
  isLoading,
  isError,
  activeSessionId: activeSessionIdProp,
  onSelect,
  onNewSession,
}: ConversationSidebarProps) {
  const { roles } = useChatUser();
  const storeActiveId = useChatSessionStore((s) => s.activeSessionId);
  const activeSessionId = activeSessionIdProp !== undefined ? activeSessionIdProp : storeActiveId;
  const isSuperAdmin = roles?.includes('SUPER_ADMIN') ?? false;

  const [filters, setFilters] = useState<FilterBarValue[]>([]);

  const { sorted, sort, handleSort } = useClientSort(sessions, SORT_FIELD_DEFS, {
    field: 'lastActivityAt',
    dir: 'desc',
  });

  const filterDefs = useMemo<FilterBarDef[]>(() => {
    const agents = [...new Set(sessions.map((s) => s.agentName).filter(Boolean))].sort();
    const statuses: SessionStatus[] = ['active', 'closed', 'error'];
    const tenants = [...new Set(sessions.map((s) => s.tenantSlug).filter(Boolean))].sort();

    return [
      {
        key: 'name',
        label: 'Name',
        type: 'text',
        icon: <Search className="mr-2 size-4" />,
      },
      {
        key: 'agent',
        label: 'Agent',
        type: 'select',
        icon: <Tag className="mr-2 size-4" />,
        options: agents.map((a) => ({ value: a, label: a })),
      },
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        icon: <Activity className="mr-2 size-4" />,
        options: statuses.map((s) => ({ value: s, label: s })),
      },
      ...(isSuperAdmin && tenants.length > 0
        ? [
            {
              key: 'tenant',
              label: 'Tenant',
              type: 'select' as const,
              options: tenants.map((t) => ({ value: t, label: t })),
            },
          ]
        : []),
    ];
  }, [sessions, isSuperAdmin]);

  const filtered = useMemo(
    () => sorted.filter((s) => filterSession(s, filters)),
    [sorted, filters],
  );

  return (
    <div className="flex flex-col h-full border rounded-xl bg-card overflow-hidden">
      <div className="px-3 py-2 border-b shrink-0">
        <FilterBar
          defs={filterDefs}
          value={filters}
          onChange={setFilters}
          layout="split"
          sortFields={FILTER_BAR_SORT_FIELDS}
          sort={sort}
          onSort={handleSort}
          hideClearAll
          leading={
            <div className="flex items-center gap-1 min-w-0">
              {filters.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setFilters([])}
                  className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  Clear all
                </button>
              ) : (
                <>
                  <p className="flex-1 text-sm font-semibold truncate">
                    Conversations
                    {sessions.length > 0 && (
                      <span className="ml-1.5 text-xs font-normal text-muted-foreground">
                        {sessions.length}
                      </span>
                    )}
                  </p>
                  {onNewSession && (
                    <Button
                      type="button"
                      appearance="ghost"
                      size="icon"
                      className="size-7 shrink-0"
                      aria-label="New conversation"
                      onClick={onNewSession}
                    >
                      <Plus className="size-3.5" />
                    </Button>
                  )}
                </>
              )}
            </div>
          }
        />
      </div>

      <div className="flex-1 overflow-y-auto min-w-0">
        <div className="p-2 space-y-3">
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton placeholder, no stable id
              <div key={i} className="px-3 py-2.5 space-y-1.5">
                <Skeleton className="h-3.5 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            ))
          ) : isError ? (
            <div className="py-6 px-3 text-center">
              <p className="text-xs text-destructive">Failed to load conversations</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-8">
              <EmptyState
                heading={filters.length > 0 ? 'No results' : 'No conversations'}
                description={
                  filters.length > 0 ? 'Try adjusting your filters' : 'Start a new conversation'
                }
              />
            </div>
          ) : (
            <div className="space-y-0.5">
              {filtered.map((session) => (
                <SessionItem
                  key={session.id}
                  session={session}
                  isActive={activeSessionId === session.id}
                  isSuperAdmin={isSuperAdmin}
                  onSelect={onSelect}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
